import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Box, Text, Flex, VStack, Input, Textarea, Select, Slider, SliderTrack,
  SliderFilledTrack, SliderThumb, Switch, SimpleGrid, Divider,
  Collapse, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody, Button, Badge, Image, InputGroup,
  InputLeftElement, useToast, Spinner,
} from '@chakra-ui/react'
import { ChevronDown, ChevronRight, AlignLeft, AlignCenter, AlignRight, FolderOpen, X, Search, Upload, Trash2, Plus, Bold, Italic, Underline, Strikethrough } from 'lucide-react'
import { BLOCK_LABELS, GRADIENT_PRESETS, FONT_FAMILIES, STOCK_IMAGES, loadGoogleFont } from './blockDefaults'
import useEmailDesignerStore from './emailDesignerStore'
import { registerActiveField } from './activeFieldTracker'
import { listPublishedStories } from './storyBlocksClient'
import { CONDITION_OPS, hasCondition, describeCondition } from './emailConditions'
import { fetchWrapper } from '../../../../helpers'
import { sanitizeTag, deriveTrackingId, ensureUniqueTrackingId, collectTrackingIds, MAX_TAG_LEN } from './trackingTags'
import { PRIMARY_BG } from '../../../../theme/brand';
const ACCENT = PRIMARY_BG
const MAX_IMAGE_SIZE_MB = 5
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

// Shared validation for any image file picked via upload — used by both the
// Image block's own uploader and the Asset Library modal's uploader.
function validateImageFile(file, toast) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    toast({
      title: 'Unsupported file type',
      description: 'Please upload a JPG, PNG, GIF, or WEBP image.',
      status: 'error', duration: 4000, isClosable: true,
    })
    return false
  }
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
    toast({
      title: 'Image too large',
      description: `Images must be under ${MAX_IMAGE_SIZE_MB}MB (this file is ${(file.size / (1024 * 1024)).toFixed(1)}MB).`,
      status: 'error', duration: 4000, isClosable: true,
    })
    return false
  }
  return true
}

// Uploads a file to the media library backend and returns the hosted URL.
// Using a real hosted URL (instead of a blob:/data-URI) is what makes uploaded
// images actually display in sent emails and on reload.
async function uploadMediaFile(file) {
  const formData = new FormData()
  formData.append('file', file)
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/media-upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-access-token': localStorage.getItem('customer-portal'),
        'x-api-key': localStorage.getItem('api-key'),
      },
    }
  )
  const result = response.data
  if (!result?.success) throw new Error(result?.message || 'Upload failed')
  return result.fileUrl || result.url || result.data?.url
}

async function fetchMediaList() {
  const url = `${process.env.REACT_APP_API_URL}/media-list`
  const res = await fetchWrapper.get(url)
  const list = res?.files || []
  return list.map((item, i) => ({
    id: item.fileName || item.name || `media_${i}`,
    name: item.fileName || item.name || `Image ${i + 1}`,
    url: item.url,
    objectName: item.name, // full storage path — required by /delete-media
    category: 'Uploads',
    isUser: true,
  })).filter(img => img.url)
}

async function deleteMediaFile(objectName) {
  const url = `${process.env.REACT_APP_API_URL}/delete-media`
  const res = await fetchWrapper.post(url, { objectName })
  if (res?.success === false) throw new Error(res?.message || 'Delete failed')
  return res
}

/* ─── Primitives ─── */

function Section({ title, sub, defaultOpen = true, children }) {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: defaultOpen })
  return (
    <Box mb={4}>
      <Flex align="center" justify="space-between" cursor="pointer" onClick={onToggle} py="6px" _hover={{ opacity: 0.75 }}>
        <Text fontSize="10px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">{title}</Text>
        {isOpen ? <ChevronDown size={12} color="#9ca3af" /> : <ChevronRight size={12} color="#9ca3af" />}
      </Flex>
      {sub && <Text fontSize="10px" color="gray.400" mt={-1} mb={1}>{sub}</Text>}
      <Divider mb={3} borderColor="gray.100" />
      <Collapse in={isOpen} animateOpacity>
        <VStack gap={3} align="stretch">{children}</VStack>
      </Collapse>
    </Box>
  )
}

function PropRow({ label, children }) {
  return (
    <Box>
      <Text fontSize="11px" color="gray.600" mb="6px" fontWeight="600">{label}</Text>
      {children}
    </Box>
  )
}

/**
 * Sanitizes on every keystroke (never rejects) — matches sanitizeTag() exactly,
 * so what's shown here is what ships in `ses:tags`. A comma typed mid-string
 * never reaches the stored value; typing it down to nothing snaps back to the
 * last persisted (usually auto-derived) id instead of ever storing/emitting
 * an empty tag.
 */
function TrackingIdInput({ value, onChange, placeholder, isDisabled, allowEmpty = false }) {
  const [text, setText] = useState(value || '')
  useEffect(() => { setText(value || '') }, [value])
  return (
    <Input
      size="sm" borderRadius="7px" fontFamily="mono" fontSize="11.5px"
      value={text} isDisabled={isDisabled} maxLength={MAX_TAG_LEN}
      placeholder={placeholder || 'auto-generated'}
      onChange={e => {
        const clean = sanitizeTag(e.target.value)
        if (clean) { setText(clean); onChange(clean) }
        // A field that ALWAYS needs a real id (Button, header link, …) snaps
        // back to whatever was last persisted rather than ever going empty —
        // the spec's "never emit ses:tags=\"b:\"" rule. An optional override
        // (Social's per-platform field) is allowed to go empty: that's its
        // normal "use the auto-derived id instead" state.
        else if (allowEmpty) { setText(''); onChange('') }
        else { setText(value || '') }
      }}
    />
  )
}

/** Shared "Tracking ID + Exclude from click tracking" pair — one per anchor. */
function TrackingFields({ trackingId, onTrackingIdChange, noTrack, onNoTrackChange, idLabel = 'Tracking ID' }) {
  return (
    <>
      <PropRow label={idLabel}>
        <TrackingIdInput value={trackingId} onChange={onTrackingIdChange} isDisabled={noTrack} />
      </PropRow>
      <Flex align="center" justify="space-between">
        <Text fontSize="11px" color="gray.600" fontWeight="600">Exclude from click tracking</Text>
        <AccentSwitch size="sm" isChecked={!!noTrack} onChange={e => onNoTrackChange(e.target.checked)} />
      </Flex>
    </>
  )
}

// Switch used across the properties panel, styled with the panel's accent color when checked
function AccentSwitch(props) {
  return (
    <Switch
      {...props}
      sx={{ '& .chakra-switch__track[data-checked]': { bg: ACCENT }, ...props.sx }}
    />
  )
}

// Checkerboard fill = the universal "transparent" indicator.
const CHECKER = {
  backgroundImage:
    'linear-gradient(45deg,#cbd5e1 25%,transparent 25%),linear-gradient(-45deg,#cbd5e1 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#cbd5e1 75%),linear-gradient(-45deg,transparent 75%,#cbd5e1 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0,0 4px,4px -4px,-4px 0',
  backgroundColor: '#fff',
}

function ColorInput({ value, onChange, placeholder = '#000000', allowTransparent }) {
  // Backgrounds pass placeholder="Transparent" — that (or an explicit prop) turns
  // on the Transparent button so any background color can be cleared to none.
  const showTransparent = allowTransparent || String(placeholder).toLowerCase() === 'transparent'
  const isTransparent = !value
  return (
    <Flex gap={2} align="center">
      <Box position="relative" w="34px" h="34px" borderRadius="8px" overflow="hidden"
        border="1px solid" borderColor="gray.200" flexShrink={0}
        style={isTransparent ? CHECKER : { backgroundColor: value }}>
        <input type="color" value={value || '#ffffff'} onChange={e => onChange(e.target.value)}
          style={{ position: 'absolute', top: '-6px', left: '-6px', width: '150%', height: '150%', border: 'none', cursor: 'pointer', opacity: 0 }} />
      </Box>
      <Input size="sm" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} fontFamily="mono" fontSize="12px" borderRadius="7px"
        _focus={{ borderColor: ACCENT, shadow: `0 0 0 2px ${ACCENT}25` }} />
      {showTransparent && (
        <Box as="button" type="button" title="Set transparent"
          onClick={() => onChange('')} flexShrink={0}
          w="34px" h="34px" borderRadius="8px" border="1px solid" overflow="hidden"
          borderColor={isTransparent ? ACCENT : 'gray.200'}
          boxShadow={isTransparent ? `0 0 0 2px ${ACCENT}25` : 'none'}
          _hover={{ borderColor: ACCENT }} style={CHECKER}
          display="flex" alignItems="center" justifyContent="center" position="relative">
          <Box position="absolute" bottom="1px" right="0" left="0" textAlign="center">
            <Text fontSize="7.5px" fontWeight="800" color="gray.600" bg="whiteAlpha.700" lineHeight="1.1">NONE</Text>
          </Box>
        </Box>
      )}
    </Flex>
  )
}

function AlignButtons({ value, onChange }) {
  return (
    <Flex gap={1}>
      {[{ v: 'left', Icon: AlignLeft }, { v: 'center', Icon: AlignCenter }, { v: 'right', Icon: AlignRight }].map(({ v, Icon }) => (
        <Box key={v} flex={1} h="32px" display="flex" alignItems="center" justifyContent="center"
          borderRadius="7px" cursor="pointer" border="1px solid"
          borderColor={value === v ? `${ACCENT}66` : 'gray.200'}
          bg={value === v ? `${ACCENT}10` : 'transparent'}
          color={value === v ? ACCENT : 'gray.500'}
          _hover={{ bg: value === v ? `${ACCENT}10` : 'gray.50' }}
          onClick={() => onChange(v)} transition="all 0.1s">
          <Icon size={13} />
        </Box>
      ))}
    </Flex>
  )
}

function RangeSlider({ value, onChange, min = 0, max = 100, step = 1 }) {
  return (
    <Flex align="center" gap={3}>
      <Slider flex={1} min={min} max={max} step={step}
        value={Number(value) || min} onChange={v => onChange(String(v))}
        colorScheme="blue">
        <SliderTrack h="4px" borderRadius="full" bg="gray.100">
          <SliderFilledTrack bg={ACCENT} />
        </SliderTrack>
        <SliderThumb boxSize={4} shadow="sm" borderWidth="2px" style={{ borderColor: ACCENT }} />
      </Slider>
      <Box w="52px" flexShrink={0} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="7px" px={2} py={1} textAlign="center">
        <Text fontSize="12px" fontWeight="600" color="gray.700">{value}</Text>
      </Box>
    </Flex>
  )
}

function GradientPicker({ value, onChange }) {
  return (
    <Box>
      <SimpleGrid columns={6} gap={1.5} mb={2}>
        {GRADIENT_PRESETS.map(g => (
          <Box key={g.id} w="100%" pt="100%" position="relative" borderRadius="6px" cursor="pointer"
            outline={value === g.value ? `2px solid ${ACCENT}` : '2px solid transparent'}
            outlineOffset="1px" overflow="hidden"
            _hover={{ transform: 'scale(1.08)' }} transition="all 0.15s"
            onClick={() => onChange(value === g.value ? '' : g.value)}>
            <Box position="absolute" inset={0} style={{ background: g.value }} />
          </Box>
        ))}
      </SimpleGrid>
      <Input size="sm" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        fontSize="11px" fontFamily="mono" borderRadius="7px" />
    </Box>
  )
}

const FONT_CATEGORIES = ['System', 'Sans Serif', 'Serif', 'Display', 'Handwriting', 'Monospace']
const CUSTOM_FONT_VALUE = '__custom__'

function FontFamilySelect({ value, onChange }) {
  const byCategory = FONT_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = FONT_FAMILIES.filter(f => f.category === cat)
    return acc
  }, {})

  const selectedFont = FONT_FAMILIES.find(f => f.value === value)
  // Any value not in the curated list (e.g. a previously-entered custom font) is treated as custom
  const isCustomValue = Boolean(value) && !selectedFont
  const [showCustomInput, setShowCustomInput] = useState(isCustomValue)
  const [customName, setCustomName] = useState(isCustomValue ? (value.match(/'([^']+)'/)?.[1] || value) : '')

  function handleChange(e) {
    const newValue = e.target.value
    if (newValue === CUSTOM_FONT_VALUE) {
      setShowCustomInput(true)
      return
    }
    setShowCustomInput(false)
    const entry = FONT_FAMILIES.find(f => f.value === newValue)
    if (entry?.google) loadGoogleFont(entry.name)
    onChange(newValue)
  }

  function applyCustomFont(name) {
    const trimmed = name.trim()
    if (!trimmed) return
    loadGoogleFont(trimmed)
    onChange(`'${trimmed}', sans-serif`)
  }

  // Pre-load selected font if it's a Google font
  React.useEffect(() => {
    if (selectedFont?.google) loadGoogleFont(selectedFont.name)
    else if (isCustomValue) loadGoogleFont(customName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Select size="sm" value={showCustomInput ? CUSTOM_FONT_VALUE : (value || '')} onChange={handleChange}
        borderRadius="7px" fontSize="12px"
        _focus={{ borderColor: ACCENT, shadow: `0 0 0 2px ${ACCENT}25` }}>
        {FONT_CATEGORIES.map(cat => (
          byCategory[cat].length > 0 && (
            <optgroup key={cat} label={cat}>
              {byCategory[cat].map(f => (
                <option key={f.value} value={f.value}>{f.name}</option>
              ))}
            </optgroup>
          )
        ))}
        <optgroup label="Other">
          <option value={CUSTOM_FONT_VALUE}>Custom Google Font…</option>
        </optgroup>
      </Select>

      {showCustomInput && (
        <Flex gap={1.5} mt={2}>
          <Input
            size="sm" value={customName}
            onChange={e => setCustomName(e.target.value)}
            onBlur={() => applyCustomFont(customName)}
            onKeyDown={e => { if (e.key === 'Enter') applyCustomFont(customName) }}
            placeholder="Type any Google Font name…"
            borderRadius="7px" fontSize="12px"
            _focus={{ borderColor: ACCENT, shadow: `0 0 0 2px ${ACCENT}25` }}
          />
        </Flex>
      )}

      {/* Live preview strip */}
      {(selectedFont || isCustomValue) && (
        <Box
          mt={2} px={3} py={2}
          bg="gray.50" borderRadius="7px"
          border="1px solid" borderColor="gray.100"
        >
          <Text
            fontSize="15px" color="gray.700" lineHeight="1.4"
            style={{ fontFamily: selectedFont?.value || value }}
          >
            The quick brown fox
          </Text>
          <Flex align="center" gap={1.5} mt={1}>
            <Text fontSize="9px" color="gray.400" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
              {selectedFont?.name || customName}
            </Text>
            {(selectedFont?.google || isCustomValue) && (
              <Badge fontSize="8px" px={1.5} py="1px" borderRadius="4px"
                bg={`${ACCENT}15`} color={ACCENT} border={`1px solid ${ACCENT}30`}>
                Google
              </Badge>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  )
}

function PaddingControls({ props, update }) {
  return (
    <SimpleGrid columns={2} gap={2}>
      {[['Top', 'paddingTop'], ['Bottom', 'paddingBottom'], ['Left', 'paddingLeft'], ['Right', 'paddingRight']].map(([label, key]) => (
        <Box key={key}>
          <Text fontSize="10px" color="gray.500" mb={1}>{label}</Text>
          <Flex align="center" gap={1}>
            <Slider flex={1} min={0} max={80} step={2} value={Number(props[key]) || 0}
              onChange={v => update({ [key]: v })} colorScheme="blue" size="sm">
              <SliderTrack h="3px" bg="gray.100"><SliderFilledTrack bg={ACCENT} /></SliderTrack>
              <SliderThumb boxSize={3} />
            </Slider>
            <Text fontSize="11px" color="gray.500" w="28px" textAlign="right">{props[key]}</Text>
          </Flex>
        </Box>
      ))}
    </SimpleGrid>
  )
}

/* ─── Asset Library Modal ─── */
function AssetLibraryModal({ isOpen, onClose, onSelect }) {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('All')
  const [userImages, setUserImages] = useState([])
  const [loadingLibrary, setLoadingLibrary] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const fileRef = useRef(null)
  const toast = useToast()

  const loadLibrary = React.useCallback(() => {
    setLoadingLibrary(true)
    return fetchMediaList()
      .then(list => setUserImages(list))
      .catch(() => {})
      .finally(() => setLoadingLibrary(false))
  }, [])

  useEffect(() => {
    if (isOpen) loadLibrary()
  }, [isOpen, loadLibrary])

  const allImages = [...STOCK_IMAGES, ...userImages]
  const categories = ['All', ...new Set(allImages.map(img => img.category))]

  const filtered = allImages.filter(img => {
    const matchCat = category === 'All' || img.category === category
    const matchSearch = !search || img.name.toLowerCase().includes(search.toLowerCase())
    return matchCat && matchSearch
  })

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!validateImageFile(file, toast)) { e.target.value = ''; return }
    setUploading(true)
    try {
      await uploadMediaFile(file)
      await loadLibrary() // refetch so the new image has its real objectName (needed to delete it later)
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 4000, isClosable: true })
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  async function handleDelete(img) {
    if (!img.objectName) {
      // Not a backend-tracked asset (e.g. a stock image) — just hide it locally
      setUserImages(prev => prev.filter(u => u.id !== img.id))
      return
    }
    setDeletingId(img.id)
    try {
      await deleteMediaFile(img.objectName)
      setUserImages(prev => prev.filter(u => u.id !== img.id))
    } catch (error) {
      toast({ title: 'Delete failed', description: error.message, status: 'error', duration: 4000, isClosable: true })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px" overflow="hidden" maxH="80vh" display="flex" flexDir="column">
        <ModalHeader py={3.5} px={5} borderBottom="1px solid" borderColor="gray.100" fontSize="14px" fontWeight="700">
          <Flex align="center" gap={2}>
            <FolderOpen size={16} color={ACCENT} />
            <Text>Image Library</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={3} right={4} />

        {/* Toolbar */}
        <Box px={5} py={3} borderBottom="1px solid" borderColor="gray.100">
          <Flex gap={3} align="center">
            <InputGroup size="sm" flex={1}>
              <InputLeftElement pointerEvents="none">
                <Search size={13} color="#9ca3af" />
              </InputLeftElement>
              <Input
                pl={8} placeholder="Search images…" value={search}
                onChange={e => setSearch(e.target.value)}
                borderRadius="8px" fontSize="12px"
                _focus={{ borderColor: ACCENT }}
              />
            </InputGroup>
            <Select size="sm" value={category} onChange={e => setCategory(e.target.value)}
              w="140px" borderRadius="8px" fontSize="12px">
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Button size="sm" leftIcon={uploading ? <Spinner size="xs" /> : <Upload size={12} />} h="32px" px={3}
              fontSize="12px" fontWeight="600" isDisabled={uploading}
              bg={ACCENT} color="white" _hover={{ opacity: 0.9 }}
              onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
          </Flex>
        </Box>

        {/* Grid */}
        <ModalBody overflowY="auto" px={5} py={4}>
          {loadingLibrary ? (
            <Box textAlign="center" py={12}>
              <Spinner size="md" color={ACCENT} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text fontSize="13px" color="gray.400">No images found</Text>
            </Box>
          ) : (
            <SimpleGrid columns={3} gap={3}>
              {filtered.map(img => (
                <Box key={img.id} position="relative" borderRadius="10px" overflow="hidden"
                  border="1.5px solid" borderColor="gray.200"
                  cursor="pointer" _hover={{ borderColor: ACCENT, shadow: `0 0 0 2px ${ACCENT}33` }}
                  transition="all 0.15s" onClick={() => onSelect(img.url)}>
                  <Box h="120px" overflow="hidden">
                    <Image src={img.url} alt={img.name} w="100%" h="100%" objectFit="cover" />
                  </Box>
                  <Box px={2.5} py={2} bg="white">
                    <Text fontSize="11px" fontWeight="600" color="gray.700" noOfLines={1}>{img.name}</Text>
                    <Badge fontSize="9px" colorScheme="gray" mt={0.5}>{img.category}</Badge>
                  </Box>
                  {img.isUser && (
                    <Box position="absolute" top={1.5} right={1.5}
                      w="20px" h="20px" borderRadius="full" bg="red.500"
                      display="flex" alignItems="center" justifyContent="center"
                      cursor="pointer" _hover={{ bg: 'red.600' }}
                      onClick={e => { e.stopPropagation(); handleDelete(img) }}>
                      {deletingId === img.id ? <Spinner size="xs" color="white" /> : <X size={11} color="white" />}
                    </Box>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

/* ─── Block panels ─── */

// ── Header bar panel (Klaviyo parity) ────────────────────────────────────────
let _hlSeq = 0
const newHeaderLinkId = () => `hl_${Date.now()}_${++_hlSeq}`

function StyleToggle({ active, onClick, Icon, label }) {
  return (
    <Box as="button" type="button" aria-label={label} onClick={onClick}
      flex={1} h="30px" display="flex" alignItems="center" justifyContent="center"
      borderRadius="7px" border="1px solid"
      borderColor={active ? `${ACCENT}66` : 'gray.200'}
      bg={active ? `${ACCENT}10` : 'transparent'} color={active ? ACCENT : 'gray.500'}
      _hover={{ bg: active ? `${ACCENT}10` : 'gray.50' }} transition="all 0.1s">
      <Icon size={13} />
    </Box>
  )
}

/**
 * Comma-separated options editor for a dropdown field (ET_024).
 *
 * The previous version was fully controlled off the parsed array:
 *   value={options.join(', ')}  onChange={split(',').filter(Boolean)}
 * Typing the comma that starts a second option produced ['Option 1', ''],
 * filter(Boolean) dropped the empty tail, and the re-render wrote back
 * "Option 1" — erasing the comma as fast as it was typed, so a second option
 * could never be entered.
 *
 * Keeping the raw text locally lets the user type freely (commas, trailing
 * spaces, empty segments mid-edit); only the committed array is cleaned. Keyed
 * by field id upstream so switching fields re-seeds the buffer.
 */
function OptionsInput({ options, onCommit }) {
  const [text, setText] = useState(() => (options || []).join(', '))
  const parse = raw => raw.split(',').map(s => s.trim()).filter(Boolean)
  return (
    <Input
      size="sm" mb={1.5} borderRadius="7px"
      placeholder="Option 1, Option 2, …"
      value={text}
      onChange={e => {
        setText(e.target.value)
        onCommit(parse(e.target.value))
      }}
      onBlur={() => setText(parse(text).join(', '))}
    />
  )
}

function SegPills({ value, options, onChange }) {
  return (
    <Flex gap={1}>
      {options.map(([v, label]) => (
        <Box as="button" type="button" key={v} onClick={() => onChange(v)} flex={1} h="30px"
          fontSize="11px" fontWeight="600" borderRadius="7px" border="1px solid"
          display="flex" alignItems="center" justifyContent="center"
          borderColor={value === v ? `${ACCENT}66` : 'gray.200'}
          bg={value === v ? `${ACCENT}10` : 'transparent'} color={value === v ? ACCENT : 'gray.500'}
          _hover={{ bg: value === v ? `${ACCENT}10` : 'gray.50' }} transition="all 0.1s">
          {label}
        </Box>
      ))}
    </Flex>
  )
}

function HeaderBarPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const fileRef = useRef(null)
  const mobileFileRef = useRef(null)
  const toast = useToast()
  const [uploading, setUploading] = useState(false)
  const [uploadingMobile, setUploadingMobile] = useState(false)

  // ET_031: the mobile logo only had a URL box — no way to upload a file, unlike the
  // desktop logo. Parameterised by prop key so both use one upload path.
  async function uploadLogoTo(e, propKey, setBusy) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!validateImageFile(file, toast)) { e.target.value = ''; return }
    setBusy(true)
    try {
      const fileUrl = await uploadMediaFile(file)
      u({ [propKey]: fileUrl })
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 4000, isClosable: true })
    } finally {
      setBusy(false)
      e.target.value = ''
    }
  }

  const handleLogoUpload = e => uploadLogoTo(e, 'logoSrc', setUploading)
  const handleMobileLogoUpload = e => uploadLogoTo(e, 'mobileLogoSrc', setUploadingMobile)

  const links = p.links || []
  const setLinks = next => u({ links: next })
  // A row added here bypasses block creation entirely, so it needs its own
  // tracking id right now — otherwise it ships with none until someone
  // happens to type into the Tracking ID field.
  const deriveNewLinkId = (label) => {
    const existing = new Set(collectTrackingIds(useEmailDesignerStore.getState().blocks))
    return ensureUniqueTrackingId(deriveTrackingId(label), existing)
  }
  const addLink = kind => setLinks([...links, kind === 'image'
    ? { id: newHeaderLinkId(), kind: 'image', src: '', alt: '', url: '', trackingId: deriveNewLinkId('image_link'), noTrack: false }
    : { id: newHeaderLinkId(), kind: 'link', label: 'Link', url: '#', trackingId: deriveNewLinkId('link'), noTrack: false }])
  const updateLink = (id, patch) => setLinks(links.map(l => l.id === id ? { ...l, ...patch } : l))
  const removeLink = id => setLinks(links.filter(l => l.id !== id))

  return (
    <>
      <Section title="Layout">
        <PropRow label="Desktop layout">
          <Select size="sm" value={p.desktopLayout} onChange={e => u({ desktopLayout: e.target.value })} borderRadius="7px">
            <option value="logo_stacked">Logo stacked</option>
            <option value="logo_inline">Logo inline</option>
            <option value="logo_centered">Logo centered</option>
            <option value="links_only">Links and buttons only</option>
          </Select>
        </PropRow>
        <PropRow label="Mobile layout">
          <Select size="sm" value={p.mobileLayout} onChange={e => u({ mobileLayout: e.target.value })} borderRadius="7px">
            <option value="stack_all">Stack all</option>
            <option value="inline">Keep inline</option>
          </Select>
        </PropRow>
        <Flex align="center" justify="space-between">
          <Text fontSize="11px" color="gray.600" fontWeight="600">Use same logo as desktop</Text>
          <AccentSwitch size="sm" isChecked={p.useSameLogoAsDesktop} onChange={e => u({ useSameLogoAsDesktop: e.target.checked })} />
        </Flex>
        {/* ET_029: turning the switch off previously revealed nothing, so a separate
            mobile logo could never actually be set even though the model had the field. */}
        {p.useSameLogoAsDesktop === false && (
          <>
            <Flex gap={2} mt={2}>
              <Button size="sm" flex={1} leftIcon={uploadingMobile ? <Spinner size="xs" /> : <Upload size={12} />}
                variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px" isDisabled={uploadingMobile}
                _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={() => mobileFileRef.current?.click()}>
                {uploadingMobile ? 'Uploading…' : 'Upload mobile logo'}
              </Button>
              {p.mobileLogoSrc && (
                <Button size="sm" variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px"
                  _hover={{ borderColor: 'red.400', color: 'red.500' }} onClick={() => u({ mobileLogoSrc: '' })}>Remove</Button>
              )}
              <input ref={mobileFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleMobileLogoUpload} />
            </Flex>
            <PropRow label="Mobile logo URL">
              <Input size="sm" value={p.mobileLogoSrc || ''} onChange={e => u({ mobileLogoSrc: e.target.value })} placeholder="https://..." borderRadius="7px" />
            </PropRow>
            {p.mobileLogoSrc
              ? (
                <Box borderRadius="8px" overflow="hidden" border="1px solid" borderColor="gray.200" bg="gray.50" p={2}>
                  <Image src={p.mobileLogoSrc} alt="Mobile logo preview" maxH="48px" mx="auto" objectFit="contain" />
                </Box>
              )
              : <Text fontSize="10px" color="gray.400">Leave empty to keep using the desktop logo on mobile.</Text>}
          </>
        )}
      </Section>

      {p.desktopLayout !== 'links_only' && (
        <Section title="Logo">
          <Flex gap={2}>
            <Button size="sm" flex={1} leftIcon={uploading ? <Spinner size="xs" /> : <Upload size={12} />}
              variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px" isDisabled={uploading}
              _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={() => fileRef.current?.click()}>
              {uploading ? 'Uploading…' : 'Upload logo'}
            </Button>
            {p.logoSrc && (
              <Button size="sm" variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px"
                _hover={{ borderColor: 'red.400', color: 'red.500' }} onClick={() => u({ logoSrc: '' })}>Remove</Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
          </Flex>
          <PropRow label="Logo URL">
            <Input size="sm" value={p.logoSrc} onChange={e => u({ logoSrc: e.target.value })} placeholder="https://..." borderRadius="7px" />
          </PropRow>
          {p.logoSrc && (
            <Box borderRadius="8px" overflow="hidden" border="1px solid" borderColor="gray.200" bg="gray.50" p={2}>
              <Image src={p.logoSrc} alt="Logo preview" maxH="48px" mx="auto" objectFit="contain" />
            </Box>
          )}
          <PropRow label="Alt text">
            <Input size="sm" value={p.logoAlt} onChange={e => u({ logoAlt: e.target.value })} placeholder="Brief description" borderRadius="7px" />
          </PropRow>
          <PropRow label="Link address">
            <Input size="sm" value={p.logoLink} onChange={e => u({ logoLink: e.target.value })} placeholder="https://..." borderRadius="7px" />
          </PropRow>
          {p.logoLink && (
            <TrackingFields
              trackingId={p.logoTrackingId} onTrackingIdChange={v => u({ logoTrackingId: v })}
              noTrack={p.logoNoTrack} onNoTrackChange={v => u({ logoNoTrack: v })}
              idLabel="Logo Tracking ID"
            />
          )}
          <PropRow label="Max width (px)">
            <Input size="sm" value={p.logoMaxWidth} onChange={e => u({ logoMaxWidth: e.target.value })} placeholder="auto" borderRadius="7px" />
          </PropRow>
          <PropRow label={`Logo & link spacing: ${p.logoLinkSpacing}px`}>
            <RangeSlider value={p.logoLinkSpacing} onChange={v => u({ logoLinkSpacing: Number(v) })} min={0} max={60} />
          </PropRow>
          {p.desktopLayout !== 'logo_centered' && (
            <PropRow label="Logo alignment"><AlignButtons value={p.logoAlignment} onChange={v => u({ logoAlignment: v })} /></PropRow>
          )}
        </Section>
      )}

      <Section title="Navigation">
        <VStack align="stretch" spacing={2}>
          {links.map(l => (
            <Box key={l.id} border="1px solid" borderColor="gray.200" borderRadius="8px" p={2}>
              <Flex justify="space-between" align="center" mb={1.5}>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">{l.kind === 'image' ? 'Image' : 'Link'}</Text>
                <Box as="button" type="button" onClick={() => removeLink(l.id)} color="gray.400" _hover={{ color: 'red.500' }}><Trash2 size={13} /></Box>
              </Flex>
              {l.kind === 'image' ? (
                <>
                  <Input size="sm" mb={1.5} value={l.src} onChange={e => updateLink(l.id, { src: e.target.value })} placeholder="Image URL" borderRadius="7px" />
                  <Input size="sm" mb={1.5} value={l.alt} onChange={e => updateLink(l.id, { alt: e.target.value })} placeholder="Alt text" borderRadius="7px" />
                  <Input size="sm" mb={1.5} value={l.url} onChange={e => updateLink(l.id, { url: e.target.value })} placeholder="Link URL (optional)" borderRadius="7px" />
                </>
              ) : (
                <>
                  <Input size="sm" mb={1.5} value={l.label} onChange={e => updateLink(l.id, { label: e.target.value })} placeholder="Label" borderRadius="7px" />
                  <Input size="sm" mb={1.5} value={l.url} onChange={e => updateLink(l.id, { url: e.target.value })} placeholder="https://..." borderRadius="7px" />
                </>
              )}
              <TrackingFields
                trackingId={l.trackingId} onTrackingIdChange={v => updateLink(l.id, { trackingId: v })}
                noTrack={l.noTrack} onNoTrackChange={v => updateLink(l.id, { noTrack: v })}
              />
            </Box>
          ))}
        </VStack>
        <Flex gap={2}>
          <Button size="sm" flex={1} leftIcon={<Plus size={12} />} variant="outline" borderColor="gray.200"
            fontSize="11.5px" h="32px" _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={() => addLink('link')}>Add link</Button>
          <Button size="sm" flex={1} leftIcon={<Plus size={12} />} variant="outline" borderColor="gray.200"
            fontSize="11.5px" h="32px" _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={() => addLink('image')}>Add image</Button>
        </Flex>
      </Section>

      <Section title="Link Text">
        <PropRow label="Font"><FontFamilySelect value={p.linkFontFamily} onChange={v => u({ linkFontFamily: v })} /></PropRow>
        <PropRow label={`Font size: ${p.linkFontSize}px`}><RangeSlider value={p.linkFontSize} onChange={v => u({ linkFontSize: Number(v) })} min={8} max={40} /></PropRow>
        <PropRow label="Color"><ColorInput value={p.linkColor} onChange={v => u({ linkColor: v })} /></PropRow>
        <PropRow label={`Letter spacing: ${p.linkLetterSpacing}px`}><RangeSlider value={p.linkLetterSpacing} onChange={v => u({ linkLetterSpacing: Number(v) })} min={0} max={10} /></PropRow>
        <PropRow label="Style">
          <Flex gap={1}>
            <StyleToggle active={p.linkBold} onClick={() => u({ linkBold: !p.linkBold })} Icon={Bold} label="Bold" />
            <StyleToggle active={p.linkItalic} onClick={() => u({ linkItalic: !p.linkItalic })} Icon={Italic} label="Italic" />
            <StyleToggle active={p.linkUnderline} onClick={() => u({ linkUnderline: !p.linkUnderline })} Icon={Underline} label="Underline" />
            <StyleToggle active={p.linkStrike} onClick={() => u({ linkStrike: !p.linkStrike })} Icon={Strikethrough} label="Strikethrough" />
          </Flex>
        </PropRow>
      </Section>

      <Section title="Link Alignment" defaultOpen={false}>
        <PropRow label="Vertical"><SegPills value={p.linkVAlign} onChange={v => u({ linkVAlign: v })} options={[['top', 'Top'], ['middle', 'Middle'], ['bottom', 'Bottom']]} /></PropRow>
        <PropRow label="Horizontal"><SegPills value={p.linkHAlign} onChange={v => u({ linkHAlign: v })} options={[['auto', 'Auto'], ['manual', 'Manual']]} /></PropRow>
        {p.linkHAlign === 'manual' && (
          <PropRow label="Align links"><AlignButtons value={p.linkAlignment} onChange={v => u({ linkAlignment: v })} /></PropRow>
        )}
      </Section>

      <Section title="Link Padding" defaultOpen={false}>
        <PropRow label={`Vertical: ${p.linkPaddingV}px`}><RangeSlider value={p.linkPaddingV} onChange={v => u({ linkPaddingV: Number(v) })} min={0} max={40} /></PropRow>
        <PropRow label={`Horizontal: ${p.linkPaddingH}px`}><RangeSlider value={p.linkPaddingH} onChange={v => u({ linkPaddingH: Number(v) })} min={0} max={40} /></PropRow>
      </Section>

      <Section title="Border & Corners" defaultOpen={false}>
        <PropRow label={`Corner radius: ${p.cornerRadius}px`}><RangeSlider value={p.cornerRadius} onChange={v => u({ cornerRadius: Number(v) })} min={0} max={40} /></PropRow>
        <PropRow label="Border style">
          <Select size="sm" value={p.borderStyle} onChange={e => u({ borderStyle: e.target.value })} borderRadius="7px">
            {['none', 'solid', 'dashed', 'dotted'].map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </Select>
        </PropRow>
        {p.borderStyle !== 'none' && (
          <>
            <PropRow label={`Border width: ${p.borderWidth}px`}><RangeSlider value={p.borderWidth} onChange={v => u({ borderWidth: Number(v) })} min={0} max={10} /></PropRow>
            <PropRow label="Border color"><ColorInput value={p.borderColor} onChange={v => u({ borderColor: v })} /></PropRow>
          </>
        )}
        <PropRow label="Drop shadow">
          <Select size="sm" value={p.dropShadow} onChange={e => u({ dropShadow: e.target.value })} borderRadius="7px">
            {[['none', 'None'], ['soft', 'Soft'], ['medium', 'Medium'], ['strong', 'Strong']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </PropRow>
      </Section>

      <Section title="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>

      <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>

      <Section title="Visibility" defaultOpen={false}>
        <SegPills value={p.visibility} onChange={v => u({ visibility: v })} options={[['both', 'All'], ['desktop', 'Desktop'], ['mobile', 'Mobile']]} />
      </Section>
    </>
  )
}

// Fetch the tenant's customer-profile attribute keys once (cached module-wide).
let _profileKeysPromise = null
function fetchProfileKeys() {
  if (!_profileKeysPromise) {
    _profileKeysPromise = fetchWrapper
      .get(`${process.env.REACT_APP_API_URL}/contacts/customer-profile-keys`)
      .then(res => (res?.data || []).map(r => (typeof r === 'string' ? r : r.key)).filter(Boolean))
      .catch(() => [])
  }
  return _profileKeysPromise
}
function useProfileKeys() {
  const [keys, setKeys] = useState([])
  useEffect(() => { let alive = true; fetchProfileKeys().then(k => { if (alive) setKeys(k) }); return () => { alive = false } }, [])
  return keys
}

// Shared across ALL blocks: show/hide this block per-recipient by a contact rule.
function ConditionalSection({ block }) {
  const profileKeys = useProfileKeys()
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const cond = p._condition || null
  const on = hasCondition(cond) || !!p._condition
  const noValue = ['exists', 'not_exists'].includes(cond?.op)
  const setCond = (patch) => updateBlock(block.id, { _condition: { op: 'is', ...(p._condition || {}), ...patch } })
  const clear = () => updateBlock(block.id, { _condition: null })

  return (
    <Section title="Conditional Visibility" defaultOpen={false}>
      <Flex align="center" justify="space-between" mb={on ? 2 : 0}>
        <Box>
          <Text fontSize="11px" color="gray.600" fontWeight="600">Only show if…</Text>
          <Text fontSize="9.5px" color="gray.400">Hide this block unless a contact rule matches</Text>
        </Box>
        <AccentSwitch size="sm" isChecked={on} onChange={e => e.target.checked ? setCond({}) : clear()} />
      </Flex>
      {on && (
        <VStack align="stretch" spacing={2}>
          {profileKeys.length > 0 ? (
            <Select size="sm" value={cond?.field || ''} onChange={e => setCond({ field: e.target.value })} borderRadius="7px" _focus={{ borderColor: ACCENT }}>
              <option value="">— Choose attribute —</option>
              {profileKeys.map(k => <option key={k} value={k}>{k}</option>)}
              {cond?.field && !profileKeys.includes(cond.field) && <option value={cond.field}>{cond.field}</option>}
            </Select>
          ) : (
            <Input size="sm" placeholder="Attribute (e.g. city, tier, first_name)" value={cond?.field || ''}
              onChange={e => setCond({ field: e.target.value })} borderRadius="7px" _focus={{ borderColor: ACCENT }} />
          )}
          <Select size="sm" value={cond?.op || 'is'} onChange={e => setCond({ op: e.target.value })} borderRadius="7px">
            {CONDITION_OPS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </Select>
          {!noValue && (
            <Input size="sm" placeholder="Value" value={cond?.value || ''} onChange={e => setCond({ value: e.target.value })} borderRadius="7px" />
          )}
          {hasCondition(cond) ? (
            <Text fontSize="10px" color="pink.600" bg="pink.50" p={1.5} borderRadius="6px">{describeCondition(cond)}</Text>
          ) : (
            // An incomplete rule is deliberately NOT emitted — see hasCondition. Say so,
            // rather than leaving the author thinking the block is already restricted.
            <Text fontSize="10px" color="orange.700" bg="orange.50" p={1.5} borderRadius="6px">
              {!cond?.field
                ? 'Choose an attribute — until the rule is complete this block is shown to everyone.'
                : 'Enter a value — until the rule is complete this block is shown to everyone.'}
            </Text>
          )}
        </VStack>
      )}
    </Section>
  )
}

function StoryPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  useEffect(() => {
    let alive = true
    setLoading(true); setErr('')
    listPublishedStories()
      .then(list => { if (alive) setStories(list) })
      .catch(e => { if (alive) setErr(e.message || 'Failed to load stories') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const onPick = (id) => {
    const s = stories.find(x => x.id === id)
    if (!s) return u({ storyId: '', storyTitle: '', poster: '', url: '', slides: [] })
    u({ storyId: s.id, storyTitle: s.title, poster: s.poster, url: s.url, slides: s.slides })
  }

  return (
    <>
      <Section title="Story">
        {loading ? <Flex justify="center" py={4}><Spinner size="sm" color="#db2777" /></Flex>
          : err ? <Text fontSize="11px" color="red.400">{err}</Text>
          : stories.length === 0 ? <Text fontSize="11px" color="gray.400">No published stories yet — publish a story in Story Builder first.</Text>
          : (
            <PropRow label="Published story">
              <Select size="sm" value={p.storyId || ''} onChange={e => onPick(e.target.value)} borderRadius="7px">
                <option value="">— Choose a story —</option>
                {stories.map(s => <option key={s.id} value={s.id}>{s.title} ({s.slideCount} pages)</option>)}
              </Select>
            </PropRow>
          )}
        {p.storyId && (
          <Box mt={2} fontSize="10.5px" color="gray.600" bg="pink.50" border="1px solid" borderColor="pink.100" borderRadius="8px" p={2} lineHeight="1.5">
            Plays as an auto-advancing <b>AMP carousel</b> inside the email in Gmail, Yahoo &amp; Mail.ru. Other inboxes show the poster + link fallback. Requires sending as an AMP email (see docs).
          </Box>
        )}
      </Section>
      {p.storyId && (
        <>
          <Section title="Appearance">
            <PropRow label={`Width: ${p.width}%`}><RangeSlider value={p.width} onChange={v => u({ width: v })} min={30} max={100} /></PropRow>
            <PropRow label={`Corner radius: ${p.borderRadius}px`}><RangeSlider value={p.borderRadius} onChange={v => u({ borderRadius: v })} min={0} max={40} /></PropRow>
            <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
            <Flex align="center" justify="space-between"><Text fontSize="11px" color="gray.600" fontWeight="600">Play badge (fallback)</Text><AccentSwitch size="sm" isChecked={p.showPlayBadge} onChange={e => u({ showPlayBadge: e.target.checked })} /></Flex>
            <Flex align="center" justify="space-between"><Text fontSize="11px" color="gray.600" fontWeight="600">Show title</Text><AccentSwitch size="sm" isChecked={p.showTitle} onChange={e => u({ showTitle: e.target.checked })} /></Flex>
          </Section>
          <Section title="Playback">
            <Flex align="center" justify="space-between" mb={2}>
              <Box>
                <Text fontSize="11px" color="gray.600" fontWeight="600">Auto-play in email</Text>
                <Text fontSize="9.5px" color="gray.400">Auto-advances the AMP carousel</Text>
              </Box>
              <AccentSwitch size="sm" isChecked={p.autoplay} onChange={e => u({ autoplay: e.target.checked })} />
            </Flex>
            {p.autoplay && (
              <PropRow label={`Seconds per slide: ${p.autoplayDelay}s`}>
                <RangeSlider value={p.autoplayDelay} onChange={v => u({ autoplayDelay: Number(v) })} min={2} max={8} step={0.5} />
              </PropRow>
            )}
          </Section>
          <Section title="Call to action">
            <Flex align="center" justify="space-between" mb={2}><Text fontSize="11px" color="gray.600" fontWeight="600">Show button</Text><AccentSwitch size="sm" isChecked={p.showCta} onChange={e => u({ showCta: e.target.checked })} /></Flex>
            {p.showCta && <PropRow label="Button label"><Input size="sm" value={p.ctaLabel} onChange={e => u({ ctaLabel: e.target.value })} borderRadius="7px" /></PropRow>}
            {p.showCta && <PropRow label="Button Color"><ColorInput value={p.ctaBackgroundColor} onChange={v => u({ ctaBackgroundColor: v })} /></PropRow>}
            {p.showCta && <PropRow label="Button Text Color"><ColorInput value={p.ctaTextColor} onChange={v => u({ ctaTextColor: v })} /></PropRow>}
          </Section>
          <Section title="Tracking" defaultOpen={false}>
            <Text fontSize="10px" color="gray.400" mb={1}>Thumbnail and button are separate links — each gets its own id.</Text>
            <TrackingFields
              idLabel="Thumbnail Tracking ID"
              trackingId={p.thumbnailTrackingId} onTrackingIdChange={v => u({ thumbnailTrackingId: v })}
              noTrack={p.thumbnailNoTrack} onNoTrackChange={v => u({ thumbnailNoTrack: v })}
            />
            <TrackingFields
              idLabel="Button Tracking ID"
              trackingId={p.trackingId} onTrackingIdChange={v => u({ trackingId: v })}
              noTrack={p.noTrack} onNoTrackChange={v => u({ noTrack: v })}
            />
          </Section>
          <Section title="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>
          <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
        </>
      )}
    </>
  )
}

let _ffSeq = 0
const newFieldId = () => `ff_${Date.now()}_${++_ffSeq}`
const FIELD_TYPES = [['text', 'Short text'], ['email', 'Email'], ['textarea', 'Long text'], ['rating', 'Rating 1–5'], ['select', 'Dropdown']]

function AmpFormPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const fields = p.fields || []
  const setFields = next => u({ fields: next })
  const addField = () => setFields([...fields, { id: newFieldId(), type: 'text', label: 'New question', name: `q${fields.length + 1}`, required: false }])
  const updField = (id, patch) => setFields(fields.map(f => f.id === id ? { ...f, ...patch } : f))
  const rmField = id => setFields(fields.filter(f => f.id !== id))

  return (
    <>
      <Section title="Form">
        <PropRow label="Title"><Input size="sm" value={p.title} onChange={e => u({ title: e.target.value })} borderRadius="7px" /></PropRow>
        <PropRow label="Intro"><Input size="sm" value={p.intro} onChange={e => u({ intro: e.target.value })} borderRadius="7px" /></PropRow>
      </Section>
      <Section title="Fields">
        <VStack align="stretch" spacing={2}>
          {fields.map(f => (
            <Box key={f.id} border="1px solid" borderColor="gray.200" borderRadius="8px" p={2}>
              <Flex justify="space-between" align="center" mb={1.5}>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">Field</Text>
                <Box as="button" type="button" onClick={() => rmField(f.id)} color="gray.400" _hover={{ color: 'red.500' }}><Trash2 size={13} /></Box>
              </Flex>
              <Input size="sm" mb={1.5} value={f.label} onChange={e => updField(f.id, { label: e.target.value })} placeholder="Question label" borderRadius="7px" />
              <Select size="sm" mb={1.5} value={f.type} onChange={e => updField(f.id, { type: e.target.value })} borderRadius="7px">
                {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
              {f.type === 'select' && (
                <OptionsInput key={f.id} options={f.options} onCommit={next => updField(f.id, { options: next })} />
              )}
              <Flex align="center" justify="space-between">
                <Text fontSize="11px" color="gray.600" fontWeight="600">Required</Text>
                <AccentSwitch size="sm" isChecked={f.required} onChange={e => updField(f.id, { required: e.target.checked })} />
              </Flex>
            </Box>
          ))}
        </VStack>
        <Button size="sm" mt={2} w="100%" leftIcon={<Plus size={12} />} variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px"
          _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={addField}>Add field</Button>
      </Section>
      <Section title="Submit">
        <PropRow label="Button label"><Input size="sm" value={p.submitLabel} onChange={e => u({ submitLabel: e.target.value })} borderRadius="7px" /></PropRow>
        <PropRow label="Success message"><Input size="sm" value={p.successMessage} onChange={e => u({ successMessage: e.target.value })} borderRadius="7px" /></PropRow>
        <PropRow label="Fallback link (non-AMP clients)">
          <Input size="sm" value={p.fallbackUrl || ''} onChange={e => u({ fallbackUrl: e.target.value })}
            placeholder="https://… hosted form" borderRadius="7px" />
          <Text fontSize="10px" color="gray.400" mt={1}>
            Outlook, Apple Mail and Gmail without AMP cannot submit a form. With a link here the
            button opens your hosted form instead; leave empty and it stays decorative.
          </Text>
        </PropRow>
      </Section>
      <Section title="Style">
        <PropRow label="Accent color"><ColorInput value={p.accentColor} onChange={v => u({ accentColor: v })} /></PropRow>
        <PropRow label={`Corner radius: ${p.borderRadius}px`}><RangeSlider value={p.borderRadius} onChange={v => u({ borderRadius: v })} min={0} max={30} /></PropRow>
        {/* ET_028: the form panel used to be hardcoded white in every renderer, so
            "Background" below only ever tinted the strip around it. */}
        <PropRow label="Form background"><ColorInput value={p.cardBackground} onChange={v => u({ cardBackground: v })} placeholder="#ffffff" /></PropRow>
      </Section>
      <Section title="Background" sub="Behind the form card."><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>
      <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
      <Box mx={4} mb={2} fontSize="10px" color="purple.600" bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="8px" p={2} lineHeight="1.5">
        Renders as an interactive <b>amp-form</b> in Gmail &amp; Yahoo. Requires sending as an AMP email + registering your sender for AMP. Other inboxes show a static version with a link.
      </Box>
    </>
  )
}

function CountdownPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const UNIT_TOGGLES = [['showDays', 'Days'], ['showHours', 'Hours'], ['showMins', 'Minutes'], ['showSecs', 'Seconds']]
  const LABEL_FIELDS = [['showDays', 'labelDays'], ['showHours', 'labelHours'], ['showMins', 'labelMins'], ['showSecs', 'labelSecs']]
  return (
    <>
      <Section title="Countdown target">
        <PropRow label="Ends at">
          <Input size="sm" type="datetime-local" value={p.targetDate || ''} onChange={e => u({ targetDate: e.target.value })} borderRadius="7px" />
        </PropRow>
        <Text fontSize="10px" color="gray.400" mt={1} lineHeight="1.5">
          Empty → 7 days from send. The timer ticks <b>live in every inbox</b> (rendered as an animated image, incl. Outlook).
        </Text>
      </Section>
      <Section title="Units">
        <SimpleGrid columns={2} spacing={2}>
          {UNIT_TOGGLES.map(([k, l]) => (
            <Flex key={k} align="center" justify="space-between">
              <Text fontSize="11px" color="gray.600" fontWeight="600">{l}</Text>
              <AccentSwitch size="sm" isChecked={p[k]} onChange={e => u({ [k]: e.target.checked })} />
            </Flex>
          ))}
        </SimpleGrid>
      </Section>
      <Section title="Labels">
        <Flex align="center" justify="space-between" mb={p.showLabels ? 2 : 0}>
          <Text fontSize="11px" color="gray.600" fontWeight="600">Show labels under units</Text>
          <AccentSwitch size="sm" isChecked={p.showLabels} onChange={e => u({ showLabels: e.target.checked })} />
        </Flex>
        {p.showLabels && LABEL_FIELDS.filter(([on]) => p[on]).map(([, key]) => (
          <PropRow key={key} label={key.replace('label', '')}>
            <Input size="sm" value={p[key] || ''} onChange={e => u({ [key]: e.target.value })} borderRadius="7px" />
          </PropRow>
        ))}
      </Section>
      <Section title="Style">
        <PropRow label="Size">
          <Select size="sm" value={p.size || 'md'} onChange={e => u({ size: e.target.value })} borderRadius="7px">
            {[['sm', 'Small'], ['md', 'Medium'], ['lg', 'Large']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
        <Flex align="center" justify="space-between" mt={2}>
          <Box>
            <Text fontSize="11px" color="gray.600" fontWeight="600">7-segment ghost digits</Text>
            <Text fontSize="9.5px" color="gray.400">Faint unlit segments (classic LCD look)</Text>
          </Box>
          <AccentSwitch size="sm" isChecked={p.lcd} onChange={e => u({ lcd: e.target.checked })} />
        </Flex>
      </Section>
      <Section title="Colors">
        <PropRow label="Digits"><ColorInput value={p.digitColor} onChange={v => u({ digitColor: v })} /></PropRow>
        <PropRow label="Labels"><ColorInput value={p.labelColor} onChange={v => u({ labelColor: v })} /></PropRow>
        <PropRow label="Separators"><ColorInput value={p.separatorColor} onChange={v => u({ separatorColor: v })} /></PropRow>
        <PropRow label="Panel background"><ColorInput value={p.boxColor} onChange={v => u({ boxColor: v })} /></PropRow>
      </Section>
      <Section title="Link" defaultOpen={false}>
        <PropRow label="On click, open">
          <Input size="sm" value={p.link || ''} onChange={e => u({ link: e.target.value })} placeholder="https://…" borderRadius="7px" />
        </PropRow>
        {p.link && (
          <TrackingFields
            trackingId={p.trackingId} onTrackingIdChange={v => u({ trackingId: v })}
            noTrack={p.noTrack} onNoTrackChange={v => u({ noTrack: v })}
          />
        )}
      </Section>
      <Section title="Block Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" allowTransparent /></Section>
      <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function DropShadowPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Shadow">
        <PropRow label="Shadow intensity">
          <Select size="sm" value={p.shadowColor} onChange={e => u({ shadowColor: e.target.value })} borderRadius="7px">
            {[['light', 'Light'], ['medium', 'Medium'], ['dark', 'Dark']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </PropRow>
      </Section>
      <Section title="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>
      <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function HeadingPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Content">
        <PropRow label="Text">
          <Input size="sm" value={p.content} onChange={e => u({ content: e.target.value })} borderRadius="7px" _focus={{ borderColor: ACCENT }}
            onFocus={e => registerActiveField(e.target, block.id)} />
        </PropRow>
        <PropRow label="HTML Tag">
          <Select size="sm" value={p.tag} onChange={e => {
            const tag = e.target.value
            // Changing just the semantic tag had no visible effect since font size/weight
            // are separate controls — apply the tag's conventional scale so the switch is visible.
            const sizeByTag = { h1: '32', h2: '28', h3: '24', h4: '20', h5: '16', h6: '14' }
            const weightByTag = { h1: '700', h2: '700', h3: '600', h4: '600', h5: '600', h6: '600' }
            u({ tag, fontSize: sizeByTag[tag] || p.fontSize, fontWeight: weightByTag[tag] || p.fontWeight })
          }} borderRadius="7px">
            {['h1','h2','h3','h4','h5','h6'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </Select>
        </PropRow>
      </Section>
      <Section title="Typography">
        <PropRow label="Font Family">
          <FontFamilySelect value={p.fontFamily} onChange={v => u({ fontFamily: v })} />
        </PropRow>
        <PropRow label={`Font Size: ${p.fontSize}px`}><RangeSlider value={p.fontSize} onChange={v => u({ fontSize: v })} min={12} max={72} /></PropRow>
        <PropRow label="Font Weight">
          <Select size="sm" value={p.fontWeight} onChange={e => u({ fontWeight: e.target.value })} borderRadius="7px">
            {[['400','Regular'],['500','Medium'],['600','Semi Bold'],['700','Bold'],['800','Extra Bold']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
        <PropRow label="Text Color"><ColorInput value={p.color} onChange={v => u({ color: v })} /></PropRow>
      </Section>
      <Section title="Background">
        <PropRow label="Solid Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v, gradient: '' })} placeholder="Transparent" /></PropRow>
        <PropRow label="Gradient"><GradientPicker value={p.gradient} onChange={v => u({ gradient: v, backgroundColor: '' })} /></PropRow>
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function TextPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Content">
        <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="8px" px={3} py={2.5}>
          <Text fontSize="11px" color="blue.700" fontWeight="600">Double-click the text on the canvas to edit it</Text>
          <Text fontSize="10px" color="blue.500" mt={0.5}>
            A formatting bar appears on the widget — bold, colour, font, links. Type <Text as="span" fontFamily="mono">$name</Text> for a variable.
          </Text>
        </Box>
      </Section>
      {/*
        ET_033: font family, font size, alignment and text colour used to be repeated
        here even though the on-canvas toolbar already offers all four — two sets of
        controls for one property, with the inline one silently winning on any text
        the user had selected. Only settings the toolbar does NOT provide stay.
      */}
      <Section title="Block defaults" sub="Font, size, colour and alignment live on the toolbar that appears when you click the text.">
        <PropRow label="Line Height">
          <Select size="sm" value={p.lineHeight} onChange={e => u({ lineHeight: e.target.value })} borderRadius="7px">
            {['1.2','1.4','1.5','1.6','1.8','2.0'].map(v => <option key={v} value={v}>{v}</option>)}
          </Select>
        </PropRow>
      </Section>
      <Section title="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>
      <Section title="Spacing"><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function ImagePanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const fileRef = useRef(null)
  const toast = useToast()
  const [urlError, setUrlError] = useState('')
  const [uploading, setUploading] = useState(false)

  async function handleFileUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!validateImageFile(file, toast)) { e.target.value = ''; return }
    setUploading(true)
    try {
      const fileUrl = await uploadMediaFile(file)
      setUrlError('')
      u({ src: fileUrl })
    } catch (error) {
      toast({ title: 'Upload failed', description: error.message, status: 'error', duration: 4000, isClosable: true })
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  function checkImageUrl(url) {
    if (!url) { setUrlError(''); return }
    const probe = new window.Image()
    probe.onload = () => setUrlError('')
    probe.onerror = () => setUrlError('This image URL could not be loaded. Please check the link.')
    probe.src = url
  }

  return (
    <>
      <Section title="Image">
        <PropRow label="Image URL">
          <Input disabled size="sm" value={p.src} onChange={e => u({ src: e.target.value })}
            onBlur={e => checkImageUrl(e.target.value)}
            placeholder="https://..." borderRadius="7px" _focus={{ borderColor: ACCENT }} />
          {urlError && <Text fontSize="10.5px" color="red.500" mt={1}>{urlError}</Text>}
        </PropRow>
        <Flex gap={2}>
          <Button size="sm" flex={1} leftIcon={<FolderOpen size={12} />}
            variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px"
            _hover={{ borderColor: ACCENT, color: ACCENT }} onClick={onOpen}>
            Asset Library
          </Button>
          <Button size="sm" flex={1} leftIcon={uploading ? <Spinner size="xs" /> : <Upload size={12} />}
            variant="outline" borderColor="gray.200" fontSize="11.5px" h="32px"
            isDisabled={uploading}
            _hover={{ borderColor: ACCENT, color: ACCENT }}
            onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
        </Flex>
        {p.src && (
          <Box borderRadius="8px" overflow="hidden" border="1px solid" borderColor={urlError ? 'red.300' : 'gray.200'}>
            <Image src={p.src} alt="Preview" w="100%" h="80px" objectFit="cover"
              onError={() => setUrlError('This image URL could not be loaded. Please check the link.')} />
          </Box>
        )}
        <PropRow label="Alt Text">
          <Input size="sm" value={p.alt} onChange={e => u({ alt: e.target.value })} placeholder="Image description" borderRadius="7px" />
        </PropRow>
        <PropRow label="Link URL">
          <Input size="sm" value={p.link} onChange={e => u({ link: e.target.value })} placeholder="https://..." borderRadius="7px" />
        </PropRow>
        {p.link && (
          <TrackingFields
            trackingId={p.trackingId} onTrackingIdChange={v => u({ trackingId: v })}
            noTrack={p.noTrack} onNoTrackChange={v => u({ noTrack: v })}
          />
        )}
      </Section>
      <Section title="Appearance">
        <PropRow label={`Width: ${p.width}%`}><RangeSlider value={p.width} onChange={v => u({ width: v })} min={10} max={100} /></PropRow>
        <PropRow label={`Border Radius: ${p.borderRadius}px`}><RangeSlider value={p.borderRadius} onChange={v => u({ borderRadius: v })} min={0} max={40} /></PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
      </Section>
      <Section title="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
      <AssetLibraryModal isOpen={isOpen} onClose={onClose} onSelect={url => { u({ src: url }); onClose() }} />
    </>
  )
}

function ButtonPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Button">
        <PropRow label="Label *">
          <Input size="sm" value={p.label} onChange={e => u({ label: e.target.value })} borderRadius="7px"
            borderColor={!p.label?.trim() ? 'red.300' : undefined}
            _focus={{ borderColor: ACCENT }} />
          {!p.label?.trim() && <Text fontSize="10.5px" color="red.500" mt={1}>Button label is required</Text>}
        </PropRow>
        <PropRow label="URL"><Input size="sm" value={p.url} onChange={e => u({ url: e.target.value })} placeholder="https://..." borderRadius="7px" /></PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
      </Section>
      <Section title="Tracking" defaultOpen={false}>
        <TrackingFields
          trackingId={p.trackingId} onTrackingIdChange={v => u({ trackingId: v })}
          noTrack={p.noTrack} onNoTrackChange={v => u({ noTrack: v })}
        />
      </Section>
      <Section title="Style">
        <PropRow label="Button Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} /></PropRow>
        <PropRow label="Text Color"><ColorInput value={p.textColor} onChange={v => u({ textColor: v })} /></PropRow>
        <PropRow label={`Border Radius: ${p.borderRadius}px`}><RangeSlider value={p.borderRadius} onChange={v => u({ borderRadius: v })} min={0} max={40} /></PropRow>
        <PropRow label={`Font Size: ${p.fontSize}px`}><RangeSlider value={p.fontSize} onChange={v => u({ fontSize: v })} min={10} max={28} /></PropRow>
        <PropRow label="Font Weight">
          <Select size="sm" value={p.fontWeight} onChange={e => u({ fontWeight: e.target.value })} borderRadius="7px">
            {[['400','Regular'],['500','Medium'],['600','Semi Bold'],['700','Bold']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </PropRow>
      </Section>
      <Section title="Button Padding" defaultOpen={false}>
        <PropRow label={`Horizontal: ${p.buttonPaddingH}px`}><RangeSlider value={p.buttonPaddingH} onChange={v => u({ buttonPaddingH: v })} min={8} max={80} /></PropRow>
        <PropRow label={`Vertical: ${p.buttonPaddingV}px`}><RangeSlider value={p.buttonPaddingV} onChange={v => u({ buttonPaddingV: v })} min={4} max={40} /></PropRow>
      </Section>
      <Section title="Block Padding" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function VideoPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Video">
        <PropRow label="YouTube Embed URL">
          <Input size="sm" value={p.videoUrl} onChange={e => u({ videoUrl: e.target.value })}
            placeholder="https://www.youtube.com/embed/..." borderRadius="7px" _focus={{ borderColor: ACCENT }} />
          <Text fontSize="10px" color="gray.400" mt={1}>Use the /embed/ URL format for YouTube videos</Text>
        </PropRow>
        <PropRow label="Custom Thumbnail URL (optional)">
          <Input size="sm" value={p.thumbnailUrl} onChange={e => u({ thumbnailUrl: e.target.value })}
            placeholder="https://..." borderRadius="7px" />
          <Text fontSize="10px" color="gray.400" mt={1}>Leave empty to auto-fetch from YouTube</Text>
        </PropRow>
      </Section>
      <Section title="Tracking" defaultOpen={false}>
        <Text fontSize="10px" color="gray.400" mb={1}>Thumbnail and "Watch video" link are separate — each gets its own id.</Text>
        <TrackingFields
          idLabel="Thumbnail Tracking ID"
          trackingId={p.thumbnailTrackingId} onTrackingIdChange={v => u({ thumbnailTrackingId: v })}
          noTrack={p.thumbnailNoTrack} onNoTrackChange={v => u({ thumbnailNoTrack: v })}
        />
        <TrackingFields
          idLabel="Watch Link Tracking ID"
          trackingId={p.trackingId} onTrackingIdChange={v => u({ trackingId: v })}
          noTrack={p.noTrack} onNoTrackChange={v => u({ noTrack: v })}
        />
      </Section>
      <Section title="Layout">
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
        <PropRow label="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function DividerPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Divider">
        <PropRow label="Color"><ColorInput value={p.color} onChange={v => u({ color: v })} /></PropRow>
        <PropRow label={`Thickness: ${p.thickness}px`}><RangeSlider value={p.thickness} onChange={v => u({ thickness: v })} min={1} max={8} /></PropRow>
        <PropRow label="Style">
          <Select size="sm" value={p.style} onChange={e => u({ style: e.target.value })} borderRadius="7px">
            {['solid','dashed','dotted','double'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </Select>
        </PropRow>
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function SpacerPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <Section title="Spacer">
      <PropRow label={`Height: ${p.height}px`}><RangeSlider value={p.height} onChange={v => u({ height: v })} min={8} max={200} step={4} /></PropRow>
      <PropRow label="Background Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
    </Section>
  )
}

function SocialPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)

  const platforms = [
    { key: 'facebook',  label: 'Facebook',  color: '#1877F2', enabledKey: 'facebookEnabled' },
    { key: 'twitter',   label: 'Twitter',   color: '#1DA1F2', enabledKey: 'twitterEnabled' },
    { key: 'instagram', label: 'Instagram', color: '#E4405F', enabledKey: 'instagramEnabled' },
    { key: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2', enabledKey: 'linkedinEnabled' },
    { key: 'youtube',   label: 'YouTube',   color: '#FF0000', enabledKey: 'youtubeEnabled' },
  ]

  return (
    <>
      <Section title="Platforms">
        <VStack gap={3} align="stretch">
          {platforms.map(pl => (
            <Box key={pl.key} p={3} bg="gray.50" borderRadius="8px" border="1px solid" borderColor="gray.100">
              <Flex align="center" justify="space-between" mb={p[pl.enabledKey] ? 2 : 0}>
                <Flex align="center" gap={2}>
                  <Box w={4} h={4} borderRadius="full" bg={pl.color} />
                  <Text fontSize="12px" fontWeight="600" color="gray.700">{pl.label}</Text>
                </Flex>
                <AccentSwitch size="sm" isChecked={p[pl.enabledKey] !== false}
                  onChange={e => u({ [pl.enabledKey]: e.target.checked })} />
              </Flex>
              {p[pl.enabledKey] !== false && (
                <Input size="xs" value={p[pl.key] || ''} onChange={e => u({ [pl.key]: e.target.value })}
                  placeholder={`https://${pl.key}.com/...`} borderRadius="6px"
                  _focus={{ borderColor: ACCENT }} />
              )}
            </Box>
          ))}
        </VStack>
      </Section>
      <Section title="Style">
        <PropRow label={`Icon Size: ${p.iconSize}px`}><RangeSlider value={p.iconSize} onChange={v => u({ iconSize: v })} min={20} max={60} /></PropRow>
        <PropRow label="Icon Color">
          <ColorInput value={p.iconColor} onChange={v => u({ iconColor: v })} placeholder="Brand colors" />
          {p.iconColor && (
            <Text
              fontSize="11px" color={ACCENT} fontWeight="600" mt={1.5} cursor="pointer" w="fit-content"
              onClick={() => u({ iconColor: '' })}
            >
              Use real brand colors
            </Text>
          )}
        </PropRow>
        <PropRow label={`Gap: ${p.gap}px`}><RangeSlider value={p.gap} onChange={v => u({ gap: v })} min={4} max={32} /></PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
        <PropRow label="Background"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
      </Section>
      <Section title="Advanced" defaultOpen={false} sub="Tracking ids are auto-generated per platform — override only if you need a specific value.">
        {platforms.filter(pl => p[pl.enabledKey] !== false && p[pl.key]).map(pl => (
          <PropRow key={pl.key} label={`${pl.label} Tracking ID`}>
            <TrackingIdInput
              value={p[`${pl.key}TrackingId`]}
              onChange={v => u({ [`${pl.key}TrackingId`]: v })}
              placeholder={`social_${pl.key}`}
              allowEmpty
            />
          </PropRow>
        ))}
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function FooterPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <>
      <Section title="Content">
        <PropRow label="HTML Content">
          <Textarea size="sm" value={p.content} onChange={e => u({ content: e.target.value })}
            rows={5} fontSize="12px" fontFamily="mono" borderRadius="7px" resize="vertical" />
        </PropRow>
      </Section>
      <Section title="Unsubscribe Link">
        <PropRow label="Label">
          <Input size="sm" value={p.unsubscribeLabel || ''} onChange={e => u({ unsubscribeLabel: e.target.value })} borderRadius="7px" />
        </PropRow>
        <PropRow label="URL">
          <Input size="sm" value={p.unsubscribeUrl || ''} onChange={e => u({ unsubscribeUrl: e.target.value })} placeholder="https://..." borderRadius="7px" />
        </PropRow>
        <TrackingFields
          trackingId={p.unsubscribeTrackingId} onTrackingIdChange={v => u({ unsubscribeTrackingId: v })}
          noTrack={p.unsubscribeNoTrack} onNoTrackChange={v => u({ unsubscribeNoTrack: v })}
        />
      </Section>
      <Section title="Style">
        <PropRow label={`Font Size: ${p.fontSize}px`}><RangeSlider value={p.fontSize} onChange={v => u({ fontSize: v })} min={8} max={20} /></PropRow>
        <PropRow label="Text Color"><ColorInput value={p.color} onChange={v => u({ color: v })} /></PropRow>
        <PropRow label="Alignment"><AlignButtons value={p.alignment} onChange={v => u({ alignment: v })} /></PropRow>
        <PropRow label="Background Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
    </>
  )
}

function HtmlPanel({ block }) {
  const { updateBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  return (
    <Section title="HTML Code">
      <PropRow label="Custom HTML">
        {/* ET_007: registering makes the sidebar's insert-variable land at the caret. */}
        <Textarea size="sm" value={p.content} onChange={e => u({ content: e.target.value })}
          onFocus={e => registerActiveField(e.target, block.id)}
          rows={10} fontSize="11px" fontFamily="mono" borderRadius="7px" resize="vertical" _focus={{ borderColor: ACCENT }} />
      </PropRow>
      <PropRow label="Background Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
    </Section>
  )
}

function ColumnsPanel({ block }) {
  const { updateBlock, setColumnsCount, updateColumnDef, removeBlock } = useEmailDesignerStore()
  const p = block.props
  const u = updates => updateBlock(block.id, updates)
  const count = Number(p.columns) || 2
  const columnDefs = p.columnDefs || []

  return (
    <>
      <Section title="Layout">
        <PropRow label="Number of Columns">
          <Flex gap={2}>
            {[1,2,3].map(n => (
              <Box key={n} flex={1} py={2} textAlign="center" cursor="pointer"
                borderRadius="7px" border="1.5px solid"
                borderColor={count === n ? `${ACCENT}66` : 'gray.200'}
                bg={count === n ? `${ACCENT}10` : 'transparent'}
                color={count === n ? ACCENT : 'gray.600'}
                fontWeight={count === n ? '700' : '500'} fontSize="13px"
                onClick={() => setColumnsCount(block.id, n)} transition="all 0.1s">
                {n}
              </Box>
            ))}
          </Flex>
        </PropRow>
        <PropRow label={`Column Gap: ${p.gap}px`}><RangeSlider value={p.gap} onChange={v => u({ gap: v })} min={0} max={48} /></PropRow>
        <PropRow label="Background Color"><ColorInput value={p.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" /></PropRow>
      </Section>
      <Section title="Spacing" defaultOpen={false}><PaddingControls props={p} update={u} /></Section>
      {columnDefs.map((col, i) => (
        <Section key={col.id} title={`Column ${i + 1}`} defaultOpen={i === 0}>
          <PropRow label="Background Color">
            <ColorInput value={col.backgroundColor || ''} onChange={v => updateColumnDef(block.id, col.id, { backgroundColor: v })} placeholder="Transparent" />
          </PropRow>
          {col.blocks?.length > 0 ? (
            <VStack gap={1} align="stretch">
              {col.blocks.map(sb => (
                <Flex key={sb.id} align="center" justify="space-between"
                  px={2} py="6px" bg="gray.50" borderRadius="6px" border="1px solid" borderColor="gray.100">
                  <Text fontSize="12px" color="gray.600" fontWeight="500" textTransform="capitalize">{sb.type}</Text>
                  <Box as="button" px={1.5} py={0.5} borderRadius="4px"
                    color="gray.400" _hover={{ color: 'red.400', bg: 'red.50' }} onClick={() => removeBlock(sb.id)}>
                    <Trash2 size={11} />
                  </Box>
                </Flex>
              ))}
            </VStack>
          ) : (
            <Box py={3} textAlign="center" bg="gray.50" borderRadius="7px" border="1px dashed" borderColor="gray.200">
              <Text fontSize="11px" color="gray.400">Drop blocks onto this column</Text>
            </Box>
          )}
        </Section>
      ))}
    </>
  )
}

/* ─── Page Settings (no block selected) ─── */
function PageSettings() {
  const { emailSettings, updateEmailSettings } = useEmailDesignerStore()
  const s = emailSettings
  const u = updates => updateEmailSettings(updates)

  return (
    <>
      <Section title="Page Background">
        <Text fontSize="10.5px" color="gray.500"  mb={1}>The outer area surrounding the email</Text>
        <PropRow label="Type">
          <Flex gap={2}>
            {['solid', 'gradient'].map(t => (
              <Box key={t} flex={1} py={1.5} textAlign="center" cursor="pointer"
                borderRadius="7px" border="1.5px solid"
                borderColor={s.backgroundType === t ? `${ACCENT}66` : 'gray.200'}
                bg={s.backgroundType === t ? `${ACCENT}10` : 'transparent'}
                color={s.backgroundType === t ? ACCENT : 'gray.500'}
                fontWeight={s.backgroundType === t ? '700' : '500'} fontSize="12px"
                onClick={() => u({ backgroundType: t })} transition="all 0.1s"
                textTransform="capitalize">
                {t}
              </Box>
            ))}
          </Flex>
        </PropRow>
        {s.backgroundType === 'gradient' ? (
          <PropRow label="Gradient">
            <GradientPicker value={s.backgroundColor} onChange={v => u({ backgroundColor: v })} />
          </PropRow>
        ) : (
          <PropRow label="Color">
            <ColorInput value={s.backgroundColor} onChange={v => u({ backgroundColor: v })} placeholder="Transparent" />
          </PropRow>
        )}
      </Section>

      <Section title="Email Body">
        <Text fontSize="10.5px" color="gray.500"  mb={1}>The white email content area</Text>
        <PropRow label="Type">
          <Flex gap={2}>
            {['solid', 'gradient'].map(t => (
              <Box key={t} flex={1} py={1.5} textAlign="center" cursor="pointer"
                borderRadius="7px" border="1.5px solid"
                borderColor={s.contentBackgroundType === t ? `${ACCENT}66` : 'gray.200'}
                bg={s.contentBackgroundType === t ? `${ACCENT}10` : 'transparent'}
                color={s.contentBackgroundType === t ? ACCENT : 'gray.500'}
                fontWeight={s.contentBackgroundType === t ? '700' : '500'} fontSize="12px"
                onClick={() => u({ contentBackgroundType: t })} transition="all 0.1s"
                textTransform="capitalize">
                {t}
              </Box>
            ))}
          </Flex>
        </PropRow>
        {s.contentBackgroundType === 'gradient' ? (
          <PropRow label="Gradient">
            <GradientPicker value={s.contentBackground} onChange={v => u({ contentBackground: v })} />
          </PropRow>
        ) : (
          <PropRow label="Color">
            <ColorInput value={s.contentBackground} onChange={v => u({ contentBackground: v })} placeholder="Transparent" />
          </PropRow>
        )}
      </Section>

      <Section title="Layout">
        <PropRow label={`Content Width: ${s.contentWidth}px`}>
          <RangeSlider value={s.contentWidth} onChange={v => u({ contentWidth: v })} min={320} max={800} step={10} />
        </PropRow>
      </Section>

      <Section title="Typography">
        <PropRow label="Font Family">
          <FontFamilySelect value={s.fontFamily} onChange={v => u({ fontFamily: v })} />
        </PropRow>
        <PropRow label="Link Color"><ColorInput value={s.linkColor} onChange={v => u({ linkColor: v })} /></PropRow>
      </Section>

      <Section title="Advanced" defaultOpen={false}>
        <PropRow label="Preheader Text">
          <Textarea size="sm" value={s.preheaderText} onChange={e => u({ preheaderText: e.target.value })}
            placeholder="Preview text shown in inbox before opening…"
            rows={3} fontSize="12px" borderRadius="7px" resize="vertical" />
          <Text fontSize="10px" color="gray.400" mt={1}>Hidden text shown in email client previews</Text>
        </PropRow>
      </Section>
    </>
  )
}

/* ─── Main PropertiesPanel ─── */

function PropertiesPanel() {
  const { selectedBlockId, findBlockById } = useEmailDesignerStore()
  const found = selectedBlockId ? findBlockById(selectedBlockId) : null
  const selectedBlock = found?.block || null

  function renderBlockProps(block) {
    switch (block.type) {
      case 'headerBar': return <HeaderBarPanel block={block} />
      case 'dropShadow': return <DropShadowPanel block={block} />
      case 'story':    return <StoryPanel block={block} />
      case 'ampForm':  return <AmpFormPanel block={block} />
      case 'countdown': return <CountdownPanel block={block} />
      case 'heading':  return <HeadingPanel block={block} />
      case 'text':     return <TextPanel block={block} />
      case 'image':    return <ImagePanel block={block} />
      case 'button':   return <ButtonPanel block={block} />
      case 'video':    return <VideoPanel block={block} />
      case 'divider':  return <DividerPanel block={block} />
      case 'spacer':   return <SpacerPanel block={block} />
      case 'social':   return <SocialPanel block={block} />
      case 'footer':   return <FooterPanel block={block} />
      case 'html':     return <HtmlPanel block={block} />
      case 'columns':  return <ColumnsPanel block={block} />
      default:         return <Text fontSize="sm" color="gray.400">No properties</Text>
    }
  }

  return (
    <Box
      w="300px" minW="300px" h="100%"
      bg="white"
      borderLeft="1px solid" borderColor="gray.200"
      overflowY="auto"
      display="flex" flexDir="column"
      css={{
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '4px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
      }}
    >
      {/* Header */}
      <Box px={4} py={3.5} borderBottom="1px solid" borderColor="gray.100" flexShrink={0} bg="white">
        <Flex align="center" gap={2}>
          {selectedBlock && (
            <Box w={5} h={5} borderRadius="5px" bg={`${ACCENT}15`} border={`1px solid ${ACCENT}33`}
              display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Box w="7px" h="7px" borderRadius="2px" bg={ACCENT} />
            </Box>
          )}
          <Box>
            <Text fontSize="11px" fontWeight="800" color="gray.700" letterSpacing="0.07em" textTransform="uppercase">
              {selectedBlock ? `${BLOCK_LABELS[selectedBlock.type] || 'Block'}` : 'Page Settings'}
            </Text>
            <Text fontSize="10px" color="gray.400" mt="1px" fontWeight="500">
              {selectedBlock ? 'Click canvas to deselect' : 'Background & layout settings'}
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Content */}
      <Box px={4} pt={4} pb={10} flex={1}>
        {selectedBlock ? <>{renderBlockProps(selectedBlock)}<ConditionalSection block={selectedBlock} /></> : <PageSettings />}
      </Box>
    </Box>
  )
}

export default PropertiesPanel
